"use client";
import InpSafeForm from "@/components/atoms/FormComponents/InpSafeForm";
import { onInpSafeButtonClick } from "@/helpers/inpSafeForm";

import { useCallback, useRef, useState } from "react";
import cn from "clsx";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";

const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export default function NewsletterForm({ data, styles }) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const validate = (value: string) =>
    value && !EMAIL_REGEX.test(value) ? "Please enter valid email" : "";

  const scheduleReset = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setMessage("");
      setEmailError("");
    }, 3000);
  };

  const handleBlur = () => {
    setTouched(true);
    const error = validate(email);
    setEmailError(error);
    pushToDataLayer({
      event: "newsletter_info_fill",
      field_name: "Email Id",
      error_message: error || "NA",
      section_name: "Newsletter",
      page_type: getGlobalStateValue("pageType"),
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (touched) setEmailError(validate(value));
  };

  const handleSubmit = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>) => {
      e?.preventDefault?.();
      setTouched(true);

      if (!EMAIL_REGEX.test(email)) {
        setEmailError("Please enter valid email");
        pushToDataLayer({
          event: "newsletter_info_fill",
          field_name: "Email Id",
          error_message: "Please enter valid email",
          section_name: "Newsletter",
          page_type: getGlobalStateValue("pageType"),
        });
        return;
      }

      setMessage("");
      setEmailError("");

      try {
        pushToDataLayer({
          event: "newsletter_subscribe",
          cta_text: "Subscribe",
          page_type: getGlobalStateValue("pageType"),
          section_name: "footer",
        });
        setLoading(true);

        const res = await fetch(`/api/newsletter?email=${email}`);
        const resData = await res.json();

        if (resData?.data?.newsletters?.data?.length > 0) {
          setEmailError("You are already subscribed!");
          scheduleReset();
          return;
        }

        const postRes = await fetch(`/api/newsletter`, {
          method: "POST",
          body: JSON.stringify({ email }),
          headers: { "Content-Type": "application/json" },
        });

        const postData = await postRes.json();
        if (postData?.success) {
          setMessage("Thank you for subscribing!");
          fireWebEngage(email);
          setEmail("");
          setTouched(false);
        } else {
          setEmailError("Something went wrong! Please try again later");
        }
      } catch {
        setEmailError("Something went wrong! Please try again later");
      } finally {
        setLoading(false);
        scheduleReset();
      }
    },
    [email]
  );

  const fireWebEngage = (source: string) => {
    window?.webengage?.track("Subscribe to Newsletter", {
      "Email ID": source,
    });
  };

  const validationError = touched ? validate(email) : "";
  const hasValidationErrors = !!validationError;

  return (
    <>
      <InpSafeForm
        onSubmit={handleSubmit}
        className={cn("mt-[22px] basis-full relative", styles.form)}
      >
        {data?.EmailPlaceholder?.[0]?.enabled && (
          <>
            <input
              type="text"
              name="email"
              value={email}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={data?.EmailPlaceholder?.[0]?.text}
              autoComplete="on"
              className={cn(
                "text-zinc-600 font-raleway border border-primary-blue rounded-full w-full outline-none lining-nums placeholder:text-gray-400",
                styles.newsletterInput
              )}
              id="newsletter-input"
            />
            <p className={styles.emailError}>
              {touched && validationError && <span>{validationError}</span>}
            </p>
          </>
        )}

        {data?.NewsLetterFormCTA?.enabled ? (
          <button
            type="button"
          onClick={onInpSafeButtonClick}
            className={cn(
              "bg-primary-blue absolute top-1/2 right-2 transform -translate-y-1/2",
              styles.newsletterButton
            )}
            disabled={loading}
            id="subscribe-button"
          >
            {loading ? "Submitting..." : data?.NewsLetterFormCTA?.text}
          </button>
        ) : null}
      </InpSafeForm>

      {message && <p className={styles.message}>{message}</p>}
    </>
  );
}
